import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  BRAYNEWAVE_ABN,
  EMPTY_COST_DRAFT,
  SUPPLIER_INVOICE_FIELD_LABEL,
  SUPPLIER_INVOICE_FIELDS,
  SUPPLIER_INVOICE_PDF_MAX_BYTES,
  categoryLabelProblem,
  costAud,
  costDraftFromReading,
  costIssues,
  costPeriodRange,
  costTotalOf,
  costTotals,
  formatAbn,
  mergeReadings,
  readSupplierInvoiceText,
  suggestCategoryCode,
  type CostCategory,
  type CostDraft,
  type CostPeriodChoice,
  type SupplierInvoiceField,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { CheckField, NumberField, SelectField, TextArea, TextField } from '../components/Fields.tsx';
import { ErrorState, Loading } from '../components/States.tsx';
import { fileBytes } from '../components/fileBytes.ts';
import { dateText, moneyText } from './api.ts';
import {
  costPdfLink,
  loadCategories,
  loadCosts,
  loadKeyStatus,
  readWithClaude,
  removePlatformKey,
  saveCategory,
  saveCost,
  setCostDeleted,
  setPlatformKey,
  type Cost,
  type CostPdf,
  type PlatformKeyStatus,
} from './costsApi.ts';
import { pdfLines } from './pdfText.ts';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));
type Notice = { readonly ok: boolean; readonly text: string } | null;

/** Today in this browser's time zone, as an ISO date. */
export function todayIso(now = new Date()): string {
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

const PERIODS: ReadonlyArray<readonly [CostPeriodChoice | 'custom', string]> = [
  ['this_fy', 'This financial year'],
  ['last_fy', 'Last financial year'],
  ['this_quarter', 'This quarter'],
  ['this_month', 'This month'],
  ['last_month', 'Last month'],
  ['all', 'Every cost'],
  ['custom', 'From and to dates'],
];

/** How the open form was filled, for the words above it. */
interface ReadingNote {
  readonly by: 'claude' | 'text';
  readonly unsure: readonly SupplierInvoiceField[];
  readonly missing: readonly SupplierInvoiceField[];
  /** Why Claude did not read it, when it did not. */
  readonly why: string | null;
  readonly hasText: boolean;
}

interface Editing {
  readonly id: string | null;
  readonly draft: CostDraft;
  readonly pdf: CostPdf | null;
  readonly oldPdfPath: string | null;
  readonly oldPdfName: string | null;
  readonly readBy: Cost['readBy'];
  readonly note: ReadingNote | null;
}

const draftOf = (c: Cost): CostDraft => ({
  supplier: c.supplier,
  supplierAbn: c.supplierAbn ? formatAbn(c.supplierAbn) : '',
  invoiceNumber: c.invoiceNumber,
  invoiceDate: c.invoiceDate,
  dueDate: c.dueDate ?? '',
  description: c.description,
  categoryId: c.categoryId,
  currency: c.currency,
  amountExGst: c.amountExGst,
  gst: c.gst,
  total: c.total,
  audTotal: c.audTotal,
  paidOn: c.paidOn ?? '',
  notes: c.notes,
});

/** Invoices Braynewave received: add, import from a PDF, edit, delete, filter, totals; categories; the Claude key. */
export function CostsPanel({ sb }: { readonly sb: SupabaseClient }): ReactElement {
  const [costs, setCosts] = useState<Cost[] | null>(null);
  const [categories, setCategories] = useState<CostCategory[]>([]);
  const [key, setKey] = useState<PlatformKeyStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [reading, setReading] = useState(false);
  const [over, setOver] = useState(false);
  const [period, setPeriod] = useState<CostPeriodChoice | 'custom'>('this_fy');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [supplier, setSupplier] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Cost | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const reload = useCallback(() => {
    setError(null);
    Promise.all([loadCosts(sb), loadCategories(sb), loadKeyStatus(sb)])
      .then(([c, cats, k]) => {
        setCosts(c);
        setCategories(cats);
        setKey(k);
      })
      .catch((e: unknown) => setError(message(e)));
  }, [sb]);
  useEffect(reload, [reload]);

  const run = async (action: () => Promise<unknown>, done: string): Promise<boolean> => {
    try {
      await action();
      setNotice({ ok: true, text: done });
      reload();
      return true;
    } catch (e) {
      setNotice({ ok: false, text: message(e) });
      return false;
    }
  };

  const categoryName = useMemo(() => new Map(categories.map((c) => [c.id, c.label])), [categories]);
  const categoryFor = (code: string | null): string =>
    categories.find((c) => !c.retired && c.code === code)?.id ??
    categories.find((c) => !c.retired && c.code === 'other')?.id ??
    '';

  const range =
    period === 'custom' ? (from && to ? { from, to } : null) : costPeriodRange(period, todayIso());
  const shown = useMemo(
    () =>
      (costs ?? []).filter(
        (c) =>
          (showDeleted ? c.deletedAt !== null : c.deletedAt === null) &&
          (!range || (c.invoiceDate >= range.from && c.invoiceDate <= range.to)) &&
          (!categoryId || c.categoryId === categoryId) &&
          (!supplier.trim() || c.supplier.toLowerCase().includes(supplier.trim().toLowerCase())),
      ),
    [costs, showDeleted, range?.from, range?.to, categoryId, supplier], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const totals = costTotals(shown);

  /** Read a chosen or dropped PDF into the form; nothing is saved until the admin presses Save cost. */
  const importPdf = async (file: File): Promise<void> => {
    setNotice(null);
    if (!/\.pdf$/i.test(file.name) && file.type !== 'application/pdf') {
      setNotice({ ok: false, text: 'Choose a PDF of the invoice.' });
      return;
    }
    if (file.size > SUPPLIER_INVOICE_PDF_MAX_BYTES) {
      setNotice({
        ok: false,
        text: `The PDF is ${(file.size / 1024 / 1024).toFixed(1)} MB; Teslyn takes 10 MB.`,
      });
      return;
    }
    setReading(true);
    try {
      const bytes = await fileBytes(file);
      const text = await pdfLines(bytes);
      const fromText = 'lines' in text ? readSupplierInvoiceText(text.lines, [BRAYNEWAVE_ABN]) : null;
      let values = fromText?.values ?? null;
      let unsure: readonly SupplierInvoiceField[] = fromText?.unsure ?? [];
      let by: ReadingNote['by'] = 'text';
      let why: string | null = 'error' in text ? text.error : null;
      if (key) {
        const c = await readWithClaude(sb, bytes);
        if (c.kind === 'read') {
          const merged = fromText ? mergeReadings(c.reading, fromText) : c.reading;
          values = merged.values;
          unsure = merged.unsure;
          by = 'claude';
          why = null;
        } else {
          why = c.message;
        }
      } else if (!why) {
        why = 'The platform’s Claude key is not set, so Teslyn read only the text of the PDF.';
      }
      const v = values ?? readSupplierInvoiceText([]).values;
      const code = fromText?.category ?? suggestCategoryCode(v.supplier);
      setEditing({
        id: null,
        draft: costDraftFromReading(v, categoryFor(code)),
        pdf: { bytes, name: file.name },
        oldPdfPath: null,
        oldPdfName: null,
        readBy: by,
        note: {
          by,
          unsure,
          missing: SUPPLIER_INVOICE_FIELDS.filter((k) => v[k] === null),
          why,
          hasText: fromText?.hasText ?? false,
        },
      });
    } catch (e) {
      setNotice({ ok: false, text: message(e) });
    } finally {
      setReading(false);
    }
  };

  const openPdf = async (path: string): Promise<void> => {
    try {
      window.open(await costPdfLink(sb, path), '_blank', 'noopener');
    } catch (e) {
      setNotice({ ok: false, text: message(e) });
    }
  };

  return (
    <div className="stack">
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      {error && <ErrorState message={error} onRetry={reload} />}
      {!costs && !error && <Loading what="costs" />}
      {costs && (
        <>
          {editing ? (
            <CostForm
              key={editing.id ?? 'new'}
              editing={editing}
              categories={categories}
              onCancel={() => setEditing(null)}
              onOpenPdf={openPdf}
              onSave={async (d, pdf) => {
                const ok = await run(
                  () =>
                    saveCost(sb, d, {
                      id: editing.id,
                      pdf,
                      oldPdfPath: editing.oldPdfPath,
                      readBy: editing.readBy,
                    }),
                  `Cost from ${d.supplier.trim()} saved.`,
                );
                if (ok) setEditing(null);
              }}
            />
          ) : (
            <div
              className={over ? 'photo-drop photo-drop--over' : 'photo-drop'}
              onDragOver={(e) => {
                e.preventDefault();
                setOver(true);
              }}
              onDragLeave={() => setOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setOver(false);
                const f = e.dataTransfer.files[0];
                if (f) void importPdf(f);
              }}
            >
              <p className="body">
                Record an invoice Braynewave received. Import its PDF (or drop it here) and Teslyn fills the
                form from it for you to check; nothing is saved until you press Save cost.
              </p>
              <div className="row">
                <Button variant="primary" busy={reading} onClick={() => fileInput.current?.click()}>
                  {reading ? 'Reading the PDF…' : 'Import from a PDF'}
                </Button>
                <Button
                  disabled={reading}
                  onClick={() =>
                    setEditing({
                      id: null,
                      draft: { ...EMPTY_COST_DRAFT, categoryId: categoryFor('other') },
                      pdf: null,
                      oldPdfPath: null,
                      oldPdfName: null,
                      readBy: 'typed',
                      note: null,
                    })
                  }
                >
                  Add a cost
                </Button>
              </div>
              <input
                ref={fileInput}
                type="file"
                accept="application/pdf,.pdf"
                className="sr-only"
                aria-label="Invoice PDF"
                tabIndex={-1}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) void importPdf(f);
                }}
              />
            </div>
          )}

          <div className="panel stack">
            <h2 className="heading">Costs</h2>
            <div className="form-grid">
              <SelectField label="Period" value={period} onChange={setPeriod} options={PERIODS} />
              {period === 'custom' && (
                <>
                  <TextField label="From" type="date" value={from} onChange={setFrom} />
                  <TextField label="To" type="date" value={to} onChange={setTo} />
                </>
              )}
              <SelectField
                label="Category"
                value={categoryId}
                onChange={setCategoryId}
                options={[['', 'Every category'], ...categories.map((c) => [c.id, c.label] as const)]}
              />
              <TextField label="Supplier" value={supplier} onChange={setSupplier} />
            </div>
            <CheckField label="Show deleted costs" checked={showDeleted} onChange={setShowDeleted} />
            <p className="body" role="status">
              {shown.length} {shown.length === 1 ? 'cost' : 'costs'}: {moneyText(totals.exGst, 'AUD')} ex GST,{' '}
              {moneyText(totals.gst, 'AUD')} GST, {moneyText(totals.total, 'AUD')} total.
              {totals.missingAud > 0 &&
                ` ${totals.missingAud} foreign ${totals.missingAud === 1 ? 'invoice has' : 'invoices have'} no AUD amount and ${totals.missingAud === 1 ? 'is' : 'are'} not counted.`}
            </p>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th className="label">Date</th>
                    <th className="label">Supplier</th>
                    <th className="label">Category</th>
                    <th className="label">Description</th>
                    <th className="label">Ex GST (AUD)</th>
                    <th className="label">GST (AUD)</th>
                    <th className="label">Total</th>
                    <th className="label">Paid</th>
                    <th className="label">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shown.length === 0 && (
                    <tr>
                      <td colSpan={9} className="muted">
                        No costs here.
                      </td>
                    </tr>
                  )}
                  {shown.map((c) => {
                    const aud = costAud(c);
                    return (
                      <tr key={c.id}>
                        <td>{dateText(c.invoiceDate)}</td>
                        <td>
                          {c.supplier}
                          {c.invoiceNumber && <span className="caption muted data"> {c.invoiceNumber}</span>}
                        </td>
                        <td>{categoryName.get(c.categoryId) ?? '–'}</td>
                        <td className="caption">{c.description}</td>
                        <td className="data">{moneyText(aud.exGst, 'AUD')}</td>
                        <td className="data">{moneyText(aud.gst, 'AUD')}</td>
                        <td className="data">
                          {moneyText(c.total, c.currency)}
                          {c.currency !== 'AUD' && c.audTotal !== null && (
                            <span className="caption muted"> ({moneyText(c.audTotal, 'AUD')})</span>
                          )}
                        </td>
                        <td>{c.paidOn ? dateText(c.paidOn) : 'Not paid'}</td>
                        <td className="table__actions">
                          {c.pdfPath && (
                            <Button
                              variant="quiet"
                              aria-label={`Open the PDF of ${c.supplier} ${c.invoiceNumber}`.trim()}
                              onClick={() => void openPdf(c.pdfPath!)}
                            >
                              PDF
                            </Button>
                          )}
                          {c.deletedAt ? (
                            <Button
                              variant="quiet"
                              onClick={() =>
                                void run(() => setCostDeleted(sb, c.id, false), 'Cost restored.')
                              }
                            >
                              Restore
                            </Button>
                          ) : (
                            <>
                              <Button
                                variant="quiet"
                                aria-label={`Edit the cost from ${c.supplier}`}
                                onClick={() =>
                                  setEditing({
                                    id: c.id,
                                    draft: draftOf(c),
                                    pdf: null,
                                    oldPdfPath: c.pdfPath,
                                    oldPdfName: c.pdfName,
                                    readBy: c.readBy,
                                    note: null,
                                  })
                                }
                              >
                                Edit
                              </Button>
                              <Button
                                variant="quiet"
                                aria-label={`Delete the cost from ${c.supplier}`}
                                onClick={() => setConfirmDelete(c)}
                              >
                                Delete
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {confirmDelete && (
              <div className="panel confirm" role="dialog" aria-labelledby="delete-cost-title">
                <p id="delete-cost-title" className="body">
                  Delete the cost from {confirmDelete.supplier} of {dateText(confirmDelete.invoiceDate)}? It
                  leaves the totals and the profit and loss; "Show deleted costs" lists it to restore. The
                  deletion is recorded.
                </p>
                <div className="row">
                  <Button
                    className="btn--danger"
                    onClick={() => {
                      const c = confirmDelete;
                      setConfirmDelete(null);
                      void run(() => setCostDeleted(sb, c.id, true), 'Cost deleted.');
                    }}
                  >
                    Delete cost
                  </Button>
                  <Button variant="quiet" onClick={() => setConfirmDelete(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          <CategoriesPanel
            categories={categories}
            onSave={(c, id) =>
              run(
                () => saveCategory(sb, c, id),
                id ? `Category ${c.label.trim()} saved.` : `Category ${c.label.trim()} added.`,
              )
            }
          />
          <KeyPanel
            status={key}
            onSet={(k) =>
              run(() => setPlatformKey(sb, k), 'The Claude key is stored. It is not shown again.')
            }
            onRemove={() => run(() => removePlatformKey(sb), 'The Claude key is removed.')}
          />
        </>
      )}
    </div>
  );
}

function CostForm({
  editing,
  categories,
  onSave,
  onCancel,
  onOpenPdf,
}: {
  readonly editing: Editing;
  readonly categories: readonly CostCategory[];
  readonly onSave: (d: CostDraft, pdf: CostPdf | null) => Promise<void>;
  readonly onCancel: () => void;
  readonly onOpenPdf: (path: string) => Promise<void>;
}): ReactElement {
  const [d, setD] = useState<CostDraft>(editing.draft);
  const [pdf, setPdf] = useState<CostPdf | null>(editing.pdf);
  const [issues, setIssues] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const note = editing.note;
  const set =
    <K extends keyof CostDraft>(k: K) =>
    (v: CostDraft[K]): void =>
      setD((x) => ({ ...x, [k]: v }));
  // ex GST and GST give the total; the total stays editable for an invoice that rounds differently
  const setAmount =
    (k: 'amountExGst' | 'gst') =>
    (v: number | null): void =>
      setD((x) => {
        const next = { ...x, [k]: v };
        return { ...next, total: costTotalOf(next.amountExGst, next.gst) ?? next.total };
      });
  const hint = (f: SupplierInvoiceField): string | undefined =>
    note?.unsure.includes(f)
      ? 'Check this against the PDF.'
      : note?.missing.includes(f)
        ? 'Not found in the PDF.'
        : undefined;
  const options = categories
    .filter((c) => !c.retired || c.id === d.categoryId)
    .map((c) => [c.id, c.retired ? `${c.label} (retired)` : c.label] as const);

  return (
    <form
      className="panel stack"
      aria-label="Cost"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        const found = costIssues(d);
        setIssues(found);
        if (found.length > 0) return;
        setSaving(true);
        void onSave(d, pdf).finally(() => setSaving(false));
      }}
    >
      <h2 className="heading">{editing.id ? 'Edit the cost' : 'New cost'}</h2>
      {note && (
        <div className={note.by === 'claude' ? 'notice' : 'notice notice--warn'} role="status">
          <p className="body">
            {note.by === 'claude'
              ? 'Claude read the PDF. Check every value against it before you save.'
              : note.hasText
                ? 'Teslyn read the text of the PDF. Check every value against it before you save.'
                : 'This PDF has no text to read (a scan?): type the values from it.'}
          </p>
          {note.why && <p className="caption">{note.why}</p>}
          {note.unsure.length > 0 && (
            <p className="caption">
              Check these: {note.unsure.map((f) => SUPPLIER_INVOICE_FIELD_LABEL[f]).join(', ')}.
            </p>
          )}
          {note.missing.length > 0 && (
            <p className="caption">
              Not found: {note.missing.map((f) => SUPPLIER_INVOICE_FIELD_LABEL[f]).join(', ')}.
            </p>
          )}
        </div>
      )}
      <div className="form-grid">
        <TextField
          label="Supplier"
          value={d.supplier}
          onChange={set('supplier')}
          hint={hint('supplier')}
          autoFocus
        />
        <TextField
          label="Supplier ABN"
          spellCheck={false}
          value={d.supplierAbn}
          onChange={set('supplierAbn')}
          hint={hint('supplier_abn')}
        />
        <TextField
          label="Invoice number"
          spellCheck={false}
          value={d.invoiceNumber}
          onChange={set('invoiceNumber')}
          hint={hint('invoice_number')}
        />
        <TextField
          label="Invoice date"
          type="date"
          value={d.invoiceDate}
          onChange={set('invoiceDate')}
          hint={hint('invoice_date')}
        />
        <TextField
          label="Due date"
          type="date"
          value={d.dueDate}
          onChange={set('dueDate')}
          hint={hint('due_date')}
        />
        <SelectField
          label="Category"
          value={d.categoryId}
          onChange={set('categoryId')}
          options={[['', 'Choose a category'], ...options]}
        />
        <TextField
          label="Currency"
          spellCheck={false}
          value={d.currency}
          onChange={(v) => set('currency')(v.toUpperCase())}
          hint={hint('currency')}
        />
        <NumberField
          label="Amount ex GST"
          value={d.amountExGst}
          onChange={setAmount('amountExGst')}
          step="0.01"
          hint={hint('amount_ex_gst')}
        />
        <NumberField label="GST" value={d.gst} onChange={setAmount('gst')} step="0.01" hint={hint('gst')} />
        <NumberField label="Total" value={d.total} onChange={set('total')} step="0.01" hint={hint('total')} />
        {d.currency !== 'AUD' && (
          <NumberField
            label="AUD charged"
            value={d.audTotal}
            onChange={set('audTotal')}
            step="0.01"
            hint="What the bank charged in AUD (your statement)."
          />
        )}
        <TextField
          label="Paid on"
          type="date"
          value={d.paidOn}
          onChange={set('paidOn')}
          hint={hint('paid_on')}
        />
      </div>
      <TextField
        label="Description"
        value={d.description}
        onChange={set('description')}
        hint={hint('description')}
      />
      <TextArea label="Notes" value={d.notes} onChange={set('notes')} rows={2} />
      <div className="row">
        {pdf ? (
          <p className="caption">Attached: {pdf.name}</p>
        ) : editing.oldPdfPath ? (
          <Button variant="quiet" onClick={() => void onOpenPdf(editing.oldPdfPath!)}>
            Open {editing.oldPdfName ?? 'the PDF'}
          </Button>
        ) : (
          <p className="caption muted">No PDF attached.</p>
        )}
        <label className="btn btn--quiet">
          {pdf || editing.oldPdfPath ? 'Attach another PDF' : 'Attach the PDF'}
          <input
            type="file"
            accept="application/pdf,.pdf"
            className="sr-only"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (!f) return;
              if (f.size > SUPPLIER_INVOICE_PDF_MAX_BYTES) {
                setIssues(['The PDF is larger than 10 MB.']);
                return;
              }
              void fileBytes(f).then((bytes) => setPdf({ bytes, name: f.name }));
            }}
          />
        </label>
      </div>
      {issues.length > 0 && (
        <ul className="notice notice--fail issues" role="alert">
          {issues.map((i) => (
            <li key={i} className="body">
              {i}
            </li>
          ))}
        </ul>
      )}
      <div className="row">
        <Button type="submit" variant="primary" busy={saving}>
          {saving ? 'Saving…' : 'Save cost'}
        </Button>
        <Button variant="quiet" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function CategoriesPanel({
  categories,
  onSave,
}: {
  readonly categories: readonly CostCategory[];
  readonly onSave: (c: { label: string; retired: boolean }, id: string | null) => Promise<boolean>;
}): ReactElement {
  const [label, setLabel] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; label: string } | null>(null);
  return (
    <details className="panel stack">
      <summary className="heading">Categories</summary>
      <p className="caption muted">
        Retire a category you no longer use: it stays on the costs that have it and is not offered for new
        ones.
      </p>
      <ul className="stack">
        {categories.map((c) => (
          <li key={c.id} className="row">
            {renaming?.id === c.id ? (
              <>
                <TextField
                  label={`New name for ${c.label}`}
                  value={renaming.label}
                  onChange={(v) => setRenaming({ id: c.id, label: v })}
                />
                <Button
                  onClick={() => {
                    const p = categoryLabelProblem(renaming.label, categories, c.id);
                    setProblem(p);
                    if (!p)
                      void onSave({ label: renaming.label, retired: c.retired }, c.id).then(() =>
                        setRenaming(null),
                      );
                  }}
                >
                  Save name
                </Button>
              </>
            ) : (
              <>
                <span className="body">
                  {c.label}
                  {c.retired && <span className="chip"> Retired</span>}
                </span>
                <Button
                  variant="quiet"
                  aria-label={`Rename ${c.label}`}
                  onClick={() => setRenaming({ id: c.id, label: c.label })}
                >
                  Rename
                </Button>
                <Button
                  variant="quiet"
                  aria-label={`${c.retired ? 'Use again' : 'Retire'} ${c.label}`}
                  onClick={() => void onSave({ label: c.label, retired: !c.retired }, c.id)}
                >
                  {c.retired ? 'Use again' : 'Retire'}
                </Button>
              </>
            )}
          </li>
        ))}
      </ul>
      <div className="row">
        <TextField label="New category" value={label} onChange={setLabel} />
        <Button
          onClick={() => {
            const p = categoryLabelProblem(label, categories, null);
            setProblem(p);
            if (!p) void onSave({ label, retired: false }, null).then((ok) => ok && setLabel(''));
          }}
        >
          Add category
        </Button>
      </div>
      {problem && (
        <p className="notice notice--fail" role="alert">
          {problem}
        </p>
      )}
    </details>
  );
}

function KeyPanel({
  status,
  onSet,
  onRemove,
}: {
  readonly status: PlatformKeyStatus | null;
  readonly onSet: (key: string) => Promise<boolean>;
  readonly onRemove: () => Promise<boolean>;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  return (
    <div className="panel stack">
      <h2 className="heading">Reading invoices with Claude</h2>
      <p className="caption muted">
        The platform’s own Claude (Anthropic) API key, used only to read the invoices Braynewave receives; it
        is not any organisation’s key and never reads their documents. It is stored in the vault and never
        shown again. Reading an invoice costs a few cents, billed by Anthropic to the key’s account.
      </p>
      <p className="body" role="status">
        {status
          ? `Set, ending …${status.lastFour}, on ${dateText(status.setAt)}.${status.lastOkAt ? ` Last read ${dateText(status.lastOkAt)}.` : ''}`
          : 'Not set: an imported PDF is read from its text only.'}
      </p>
      {status?.lastError && (
        <p className="notice notice--fail" role="alert">
          {status.lastError} ({dateText(status.lastErrorAt)}). Replace the key.
        </p>
      )}
      {open ? (
        <form
          className="stack"
          aria-label="Claude key"
          onSubmit={(e) => {
            e.preventDefault();
            setBusy(true);
            void onSet(value.trim())
              .then((ok) => {
                if (ok) {
                  setValue('');
                  setOpen(false);
                }
              })
              .finally(() => setBusy(false));
          }}
        >
          <TextField
            label="Claude API key"
            type="password"
            autoComplete="off"
            value={value}
            onChange={setValue}
            hint="From console.anthropic.com, API keys. It goes once to Teslyn Cloud's vault."
            autoFocus
          />
          <div className="row">
            <Button type="submit" variant="primary" busy={busy} disabled={value.trim().length < 8}>
              {busy ? 'Storing…' : 'Store the key'}
            </Button>
            <Button
              variant="quiet"
              disabled={busy}
              onClick={() => {
                setValue('');
                setOpen(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <div className="row">
          <Button onClick={() => setOpen(true)}>{status ? 'Replace the key' : 'Add the key'}</Button>
          {status &&
            (confirmRemove ? (
              <>
                <Button
                  className="btn--danger"
                  onClick={() => {
                    setConfirmRemove(false);
                    void onRemove();
                  }}
                >
                  Confirm remove
                </Button>
                <Button variant="quiet" onClick={() => setConfirmRemove(false)}>
                  Cancel
                </Button>
              </>
            ) : (
              <Button variant="quiet" onClick={() => setConfirmRemove(true)}>
                Remove the key
              </Button>
            ))}
        </div>
      )}
    </div>
  );
}
